import { Button } from "@src/lib/ui/Button";
import { Separator } from "@src/lib/ui/Separator";
import Text from "@src/lib/ui/Text";
import XIcon from "@src/lib/icons/18px/x";
import { Input } from "@src/lib/ui/Input";
import { getLinkType, humanFileSize, linkTypeCopy } from "@src/lib/utils/files";
import { FC, useState } from "react";
import { FileInput } from "./BulkUploadTrainingResourceDialog";
import { MediaPreview } from "./MediaPreview";
import { useTranslation } from "@src/features/contentDetail/utils/translationSets";

type Props = {
  file: FileInput;
  updateFileName: (id: string, language: Language, value: string) => void;
  updateFileDescription: (
    id: string,
    language: Language,
    value: string,
  ) => void;
  removeFile: (id: string) => void;
  language: Language;
  uploadProgress?: number;
  isUploading?: boolean;
};
export const UploadedFile: FC<Props> = ({
  file,
  updateFileDescription,
  updateFileName,
  removeFile,
  language,
  uploadProgress = 0,
  isUploading = false,
}) => {
  const [descriptionVisible, setDescriptionVisible] = useState(false);
  const type = file.url && getLinkType(file.url);
  const fileNameTranslation = useTranslation(file.name, language).text;
  const fileDescriptionTranslation = useTranslation(
    file.description,
    language,
  ).text;
  return (
    <div className="flex w-full flex-col rounded-2xl border border-gray-5/10 bg-white">
      <div className="flex w-full flex-1 flex-row self-stretch p-3">
        <MediaPreview url={file.url} isUploading={isUploading} />
        <div className="ml-4 flex flex-1 flex-col self-center">
          <Text ellipsis variant="compact-title" className="text-gray-12">
            {fileNameTranslation || file.name.en}
          </Text>
          {isUploading && (
            <Text variant="fine-print-description" className="text-gray-5">
              Uploading... {uploadProgress}%
            </Text>
          )}
          {!file.url && !isUploading && (
            <Text variant="fine-print-description" className="text-gray-5">
              Uploading....
            </Text>
          )}
          {file.url && (
            <div className="flex flex-row items-center space-x-2">
              <Text variant="fine-print-description" className="text-gray-5">
                {type ? linkTypeCopy[type] : "File"}
              </Text>
              <span className="size-[3px] rounded-[3px] bg-gray-5" />
              <Text variant="fine-print-description" className="text-gray-5">
                {file.size ? humanFileSize(file.size) : file.url}
              </Text>
            </div>
          )}
        </div>
        <XIcon onClick={() => removeFile(file.id)} />
      </div>

      <Separator />
      <div className="flex flex-col gap-2 self-stretch p-3">
        <Input
          value={fileNameTranslation || ""}
          onChange={(e) => {
            updateFileName(file.id, language, e.target.value);
          }}
          label="Name"
        />
        {descriptionVisible && (
          <Input
            value={fileDescriptionTranslation || ""}
            onChange={(e) => {
              updateFileDescription(file.id, language, e.target.value);
            }}
            label="Description"
            autoFocus
          />
        )}
        {!descriptionVisible && (
          <Button variant="ghost" onClick={() => setDescriptionVisible(true)}>
            + Add description
          </Button>
        )}
      </div>
    </div>
  );
};
